const LAST_RECOVERY_STORAGE_KEY = "kamae:stale-assets-recovery-at"

// A crash that comes back within this window means refreshing the assets did not help:
// automatic recovery stops there, otherwise the page would reload forever.
const AUTOMATIC_RECOVERY_COOLDOWN_MS = 60_000

const ASSET_LOAD_ERROR_PATTERNS = [
  /dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
]

let pendingRecovery: Promise<void> | null = null

// A corrupted chunk surfaces as a SyntaxError, a missing or unreachable one as an import failure.
export const isAssetLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false
  }
  return error.name === "SyntaxError" || ASSET_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(error.message))
}

export const canRecoverAutomatically = (): boolean => {
  try {
    const lastRecoveryAt = Number(sessionStorage.getItem(LAST_RECOVERY_STORAGE_KEY))
    return Date.now() - lastRecoveryAt > AUTOMATIC_RECOVERY_COOLDOWN_MS
  } catch {
    // Without storage there is no way to detect a reload loop, so never reload on our own.
    return false
  }
}

// A plain reload is not enough (KAM-3412): the browser keeps serving the broken copy from its
// HTTP cache. Re-downloading with `cache: "reload"` overwrites those entries without touching
// cookies, unlike "clear browsing data" which logs the user out.
export const recoverFromStaleAssets = (): Promise<void> => {
  pendingRecovery ??= refreshLoadedAssets().then(() => {
    rememberRecovery()
    window.location.reload()
  })
  return pendingRecovery
}

export const registerPreloadErrorRecovery = (): void => {
  window.addEventListener("vite:preloadError", () => {
    if (canRecoverAutomatically()) {
      void recoverFromStaleAssets()
    }
  })
}

const refreshLoadedAssets = async (): Promise<void> => {
  await Promise.allSettled(
    collectLoadedAssetUrls().map(url => fetch(url, { cache: "reload" }).then(response => response.arrayBuffer()))
  )
}

const collectLoadedAssetUrls = (): string[] => {
  const resourceUrls = performance.getEntriesByType("resource").map(entry => entry.name)
  const elementUrls = Array.from(
    document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>("script[src], link[href]")
  ).map(element => ("src" in element ? element.src : element.href))
  return Array.from(new Set([...resourceUrls, ...elementUrls])).filter(isOwnAsset)
}

const isOwnAsset = (url: string): boolean => {
  try {
    const { origin, pathname } = new URL(url, window.location.href)
    return origin === window.location.origin && pathname.startsWith("/assets/")
  } catch {
    return false
  }
}

const rememberRecovery = (): void => {
  try {
    sessionStorage.setItem(LAST_RECOVERY_STORAGE_KEY, String(Date.now()))
  } catch {
    // Storage unavailable: canRecoverAutomatically already refuses automatic reloads in that case.
  }
}
